import React, { useEffect, useState } from "react";
import { Puzzle, Loader2, Save, Gift, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

// إدارة الإضافات: تشغيل/إيقاف أي خدمة إضافية وتحديد سعرها دون المساس بالباقات
export const AdminAddons = () => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  const [grant, setGrant] = useState({ email: "", days: 30, addon: "super_rawaj" });
  const [granting, setGranting] = useState(false);

  const load = () => api.get("/admin/addons").then((r) => { setData(r.data); setEdits({}); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    try { await api.patch(`/admin/addons/${a.id}`, { enabled: !a.enabled }); toast.success(a.enabled ? `تم إيقاف ${a.name}` : `تم تشغيل ${a.name}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const save = async (a) => {
    const e = edits[a.id] || {};
    setSaving(a.id);
    try {
      await api.patch(`/admin/addons/${a.id}`, { price: e.price !== undefined ? Number(e.price) : undefined, yearly_price: e.yearly_price !== undefined ? Number(e.yearly_price) : undefined, name: e.name, description: e.description });
      toast.success("تم حفظ الإضافة"); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(null); }
  };
  const grantAll = async () => {
    if (!grant.email) return toast.error("أدخل بريد الحساب");
    setGranting(true);
    try {
      const { data } = await api.post("/admin/users/grant-all", { email: grant.email, days: 365 });
      toast.success(`مُنح ${data.email}: باقة الاستراتيجية + سوبر رواج + ${data.videos} فيديو + ${data.images} صورة + محفظة ${data.wallet} ريال`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setGranting(false); }
  };
  const doGrant = async () => {
    if (!grant.email.trim()) return toast.error("أدخل بريد العميل");
    setGranting(true);
    try { await api.post(`/admin/addons/${grant.addon}/grant`, { email: grant.email.trim(), days: Number(grant.days) || 30 }); toast.success("تم التفعيل للعميل ✅"); setGrant({ ...grant, email: "" }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setGranting(false); }
  };
  const ed = (a, k) => (edits[a.id]?.[k] !== undefined ? edits[a.id][k] : a[k]);
  const setEd = (a, k, v) => setEdits((p) => ({ ...p, [a.id]: { ...(p[a.id] || {}), [k]: v } }));

  if (!data) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="admin-addons">
      <div className="flex items-center gap-2 mb-1"><Puzzle className="w-5 h-5 text-accent" /><h2 className="text-lg font-bold">إدارة الإضافات (الخدمات الاختيارية)</h2></div>
      <p className="text-xs text-muted-foreground mb-5">تشغيل أو إيقاف أي خدمة إضافية وتحديد سعرها مباشرة — الباقات الأساسية لا تتأثر. الخدمة المتوقفة تختفي من واجهة العملاء فوراً.</p>
      <div className="space-y-3">
        {data.addons.map((a) => (
          <div key={a.id} className="border border-border rounded-xl p-4 grid lg:grid-cols-12 gap-3 items-start" data-testid={`addon-row-${a.id}`}>
            <div className="lg:col-span-3">
              <label className="text-[11px] text-muted-foreground">الاسم</label>
              <Input value={ed(a, "name")} onChange={(e) => setEd(a, "name", e.target.value)} className="h-9 bg-secondary border-border" data-testid={`addon-name-${a.id}`} />
              <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> مشتركون نشطون: <b className="text-foreground" data-testid={`addon-subs-${a.id}`}>{a.subscribers}</b>{a.coming_soon && " · قريباً"}</div>
            </div>
            <div className="lg:col-span-2">
              <label className="text-[11px] text-muted-foreground">السعر (ريال / {a.period_days || 30} يوم)</label>
              <Input type="number" min="0" max="5000" value={ed(a, "price")} onChange={(e) => setEd(a, "price", e.target.value)} className="h-9 bg-secondary border-border" dir="ltr" data-testid={`addon-price-${a.id}`} />
              <label className="text-[11px] text-muted-foreground mt-1 block">سنوي (0 = بلا خيار سنوي)</label>
              <Input type="number" min="0" max="50000" value={ed(a, "yearly_price") ?? 0} onChange={(e) => setEd(a, "yearly_price", e.target.value)} className="h-9 bg-secondary border-border" dir="ltr" data-testid={`addon-yearly-${a.id}`} />
            </div>
            <div className="lg:col-span-5">
              <label className="text-[11px] text-muted-foreground">الوصف للعميل</label>
              <Input value={ed(a, "description")} onChange={(e) => setEd(a, "description", e.target.value)} className="h-9 bg-secondary border-border" data-testid={`addon-desc-${a.id}`} />
            </div>
            <div className="lg:col-span-2 flex items-center gap-3 lg:justify-end pt-4">
              <div className="flex items-center gap-1.5 text-xs"><Switch checked={!!a.enabled} onCheckedChange={() => toggle(a)} data-testid={`addon-toggle-${a.id}`} />{a.enabled ? "مفعّلة" : "متوقفة"}</div>
              <Button size="sm" onClick={() => save(a)} disabled={saving === a.id || !edits[a.id]} className="gap-1" data-testid={`addon-save-${a.id}`}>
                {saving === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} حفظ
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 border-t border-border pt-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2"><Gift className="w-4 h-4 text-primary" /> تفعيل إضافة لعميل يدوياً (حتى تفعيل الدفع)</div>
        <div className="grid sm:grid-cols-4 gap-2 items-end">
          <Input value={grant.email} onChange={(e) => setGrant({ ...grant, email: e.target.value })} placeholder="بريد العميل" dir="ltr" className="bg-secondary border-border" data-testid="addon-grant-email" />
          <select value={grant.addon} onChange={(e) => setGrant({ ...grant, addon: e.target.value })} className="h-10 rounded-md border border-border bg-secondary px-3 text-sm" data-testid="addon-grant-select">
            {data.addons.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <Input type="number" value={grant.days} onChange={(e) => setGrant({ ...grant, days: e.target.value })} placeholder="الأيام (سالب للإلغاء)" dir="ltr" className="bg-secondary border-border" data-testid="addon-grant-days" />
          <Button onClick={doGrant} disabled={granting} className="gap-1" data-testid="addon-grant-btn">{granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} تفعيل</Button>
          <Button variant="outline" onClick={grantAll} disabled={granting} className="gap-1 border-accent/60 text-accent hover:bg-accent/10" title="أعلى باقة + سوبر رواج + 60 فيديو + 400 صورة + محفظة 500 ريال لسنة" data-testid="grant-all-btn">
            <Gift className="w-4 h-4" /> امنح كل المزايا (اختبار)
          </Button>
        </div>
        {data.recent_grants?.length > 0 && (
          <div className="mt-3 text-xs text-muted-foreground space-y-1" data-testid="addon-grant-log">
            {data.recent_grants.slice(0, 8).map((g) => <div key={g.id}><span dir="ltr">{g.email}</span> · {g.addon_id} · {g.days} يوم · {g.source} · <span dir="ltr">{String(g.at).slice(0, 16).replace("T", " ")}</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
};
